<template>
  <ConfirmationDialog
    width="450"
    :title="`${capitalize(actionText)} user`"
    v-model="dialog"
    v-model:error="error"
    :open-on-click="user.is_active ? true : false"
  >
    <template #activator="{ props }">
      <slot
        name="activator"
        v-bind="{
          props: {
            ...props,
            onClick: user.is_active ? props.onClick : toggleUserActive
          },
          loading: toggleUserActiveInProgress,
          active: user.is_active
        }"
      />
    </template>

    <template #message>
      Are you sure you want to
      <span class="text-primary font-weight-medium">{{ actionText }}</span> this user?
    </template>

    <template #actions>
      <v-spacer></v-spacer>
      <v-btn variant="flat" :disabled="toggleUserActiveInProgress" @click="closeDialog()">
        Cancel
      </v-btn>
      <v-btn
        :color="actionColor"
        variant="flat"
        :disabled="toggleUserActiveInProgress"
        :loading="toggleUserActiveInProgress"
        @click="toggleUserActive()"
      >
        {{ actionText }}
      </v-btn>
    </template>
  </ConfirmationDialog>
</template>
<script lang="ts" setup>
import { computed } from 'vue'

import { capitalize } from 'lodash'

import type { IUser } from '@/models/user'

import ConfirmationDialog from '../common/ConfirmationDialog.vue'
import { useActiveStateEmployee } from '@/composables/employee'
import { useActiveStateCustomer } from '@/composables/customer'

interface Props {
  user: IUser
  isCustomer?: boolean
}

const props = defineProps<Props>()

const dialog = defineModel<boolean>('dialog', { default: false })

const actionText = computed(() => (props.user.is_active ? 'deactivate' : 'activate'))
const actionColor = computed(() => (props.user.is_active ? 'error' : 'success'))

function closeDialog() {
  dialog.value = false
}

const userId = computed(() => props.user.id!)
const {
  isPending: toggleUserActiveInProgressEmployee,
  error: errorEmployee,
  mutate: mutateEmployee,
  reset: resetEmployee
} = useActiveStateEmployee(userId)

const isCustomer = computed(() => props.isCustomer)
const {
  isPending: toggleUserActiveInProgressCustomer,
  error: errorCustomer,
  mutate: mutateCustomer,
  reset: resetCustomer
} = useActiveStateCustomer(userId)

const toggleUserActiveInProgress = computed(() => {
  return isCustomer.value
    ? toggleUserActiveInProgressCustomer.value
    : toggleUserActiveInProgressEmployee.value
})
const error = computed(() => {
  return isCustomer.value ? errorCustomer.value : errorEmployee.value
})

function clearErrors() {
  resetCustomer()
  resetEmployee()
}

async function toggleUserActive() {
  clearErrors()

  const action = isCustomer.value ? mutateCustomer : mutateEmployee

  action(!props.user.is_active, {
    onSuccess: () => {
      closeDialog()
    }
  })
}
</script>
